"use client";

import type React from "react";
import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Box,
  useToast,
  HStack,
  Icon,
  CloseButton,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { callService } from "../../services/callService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface MakeOutboundCallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  agentId: string | null;
}

export const MakeOutboundCallDialog: React.FC<MakeOutboundCallDialogProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  agentId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toNumber, setToNumber] = useState("");
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Set colors based on theme
  const modalContentBg = isDark ? "gray.800" : "white";
  const labelColor = isDark ? "gray.300" : "gray.700";
  const infoTextColor = isDark ? "gray.400" : "gray.600";
  const infoBgColor = isDark ? "gray.700" : "gray.100";

  const handleSubmit = async () => {
    if (!toNumber) {
      toast({
        title: "Phone number is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!agentId) {
      toast({
        title: "No outbound agent configured",
        description:
          "Please configure an outbound agent for this phone number first",
        status: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await callService.createPhoneCall(agentId, phoneNumber, toNumber);

      toast({
        title: "Outbound call initiated",
        description: `Calling ${toNumber} from ${phoneNumber}`,
        status: "success",
        duration: 3000,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error making outbound call",
        description:
          error.response?.data?.message ||
          "An error occurred while making the call",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="500px">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={4}
          color={labelColor}
        >
          Make Outbound Call
          <CloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <FormControl mb={4}>
            <FormLabel color={labelColor}>Phone Number</FormLabel>
            <Input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="e.g. +11234567890"
              borderColor={isDark ? "gray.600" : "gray.300"}
            />
          </FormControl>

          <Box spacing={2} color={infoTextColor} fontSize="sm">
            <HStack spacing={2} color={infoTextColor}>
              <Icon as={InfoIcon} />
              <Text>International calls are currently not supported.</Text>
            </HStack>
          </Box>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!agentId}
          >
            Call
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
